// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import {
  AdvanceSearchRequestModel,
  AdvanceSearchResultModel,
  AdvanceSearchAnalyticsRequestModel,
  CitiesRequest,
  CountriesResult,
  ProductNamesRequest,
  SoftwareVersionsRequest,
  AdvanceSearchTableAnalyticsModel,
} from 'src/app/api/remote-api/.portal.generated/models';
import { MapApiService } from '../../shared/services/map-api.service';
import { getCities } from '../../../api/remote-api/.portal.generated/fn/advanced-search/get-cities';
import { getCountries } from '../../../api/remote-api/.portal.generated/fn/advanced-search/get-countries';
import { getProductNames } from '../../../api/remote-api/.portal.generated/fn/advanced-search/get-product-names';
import { getSoftwareVersions } from '../../../api/remote-api/.portal.generated/fn/advanced-search/get-software-versions';
import { getAdvancedSearchSystems } from '../../../api/remote-api/.portal.generated/fn/advanced-search/get-advanced-search-systems';
import { createAdvancedSearchSystemsAnalytics } from '../../../api/remote-api/.portal.generated/fn/advanced-search-analytics/create-advanced-search-systems-analytics';
import { createAdvancedSearchTableAnalytics } from '../../../api/remote-api/.portal.generated/fn/advanced-search-analytics/create-advanced-search-table-analytics';
import {
  BehaviorSubject,
  catchError,
  finalize,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { UserService } from './user.service';
@Injectable({
  providedIn: 'root',
})
export class AdvancedSearchService {
  private triggerCountry$ = new Subject<void>();
  private countryListSubject = new BehaviorSubject<
    CountriesResult[] | null
  >(null);
  countryList$ = this.countryListSubject.asObservable();

  private triggerCity$ = new Subject<CitiesRequest>();
  private readonly cityListSubject = new BehaviorSubject<
    { city?: string | null; countryId?: string | null }[] | null
  >(null);
  cityList$ = this.cityListSubject.asObservable();

  private cityLoadingSubject = new BehaviorSubject<boolean>(false);
  public isCityLoading$: Observable<boolean> =
    this.cityLoadingSubject.asObservable();

  private triggerProductName$ = new Subject<ProductNamesRequest>();
  private readonly productNameListSubject = new BehaviorSubject<
    { marketingProductName?: string | null; sellingBusinessLine?: string | null }[] | null
  >(null);
  productNameList$ = this.productNameListSubject.asObservable();

  private triggerSoftwareVersion$ =
    new Subject<SoftwareVersionsRequest>();
  private readonly softwareVersionListSubject = new BehaviorSubject<
    { swMainVersion?: string | null; marketingProductName?: string | null; sellingBusinessLine?: string | null }[] | null
  >(null);
  softwareVersionList$ = this.softwareVersionListSubject.asObservable();

  private triggerSystems$ = new Subject<AdvanceSearchRequestModel>();
  private systemListSubject = new BehaviorSubject<
    AdvanceSearchResultModel[] | null
  >(null);
  systemList$ = this.systemListSubject.asObservable();

  private systemCountSubject = new BehaviorSubject<number>(-1);
  systemCount$ = this.systemCountSubject.asObservable();

  private isSystemLoadingSubject = new BehaviorSubject<boolean>(false);
  isSystemLoading$ = this.isSystemLoadingSubject.asObservable();

  private triggerSystemsAnalytics$ =
    new Subject<AdvanceSearchAnalyticsRequestModel>();
  private triggerTableAnalytics$ =
    new Subject<AdvanceSearchTableAnalyticsModel>();

  constructor(
    private readonly mapApiService: MapApiService,
    private readonly userService: UserService,
  ) {
    this.triggerCountry$
      .pipe(
        switchMap(() => this.mapApiService.mapEndpoint(getCountries).request()),
      )
      .subscribe((data) => {
        this.countryListSubject.next([...data]);
      });

    this.triggerCity$
      .pipe(
        tap(() => {
          this.cityLoadingSubject.next(true);
          this.cityListSubject.next(null);
        }),
        switchMap((value: CitiesRequest) => {
          return this.mapApiService
            .mapEndpoint(getCities)
            .request({ body: value })
            .pipe(
              catchError((err) => {
                console.log('City fetch failed', err);
                return of({ cities: [] });
              }),
              finalize(() => {
                this.cityLoadingSubject.next(false);
              }),
            );
        }),
      )
      .subscribe((data) => {
        const sortedCities = [...(data.cities ?? [])].sort((a, b) =>
          (a.city ?? '').localeCompare(b.city ?? ''),
        );
        this.cityListSubject.next(sortedCities);
      });

    this.triggerProductName$
      .pipe(
        switchMap((value) => {
          this.productNameListSubject.next(null);
          return this.mapApiService
            .mapEndpoint(getProductNames)
            .request({ body: value });
        }),
      )
     .subscribe((data) => {
  this.productNameListSubject.next([...(data?.productNames ?? [])]);
});

    this.triggerSoftwareVersion$
      .pipe(
        switchMap((value) => {
          this.softwareVersionListSubject.next(null);
          return this.mapApiService
            .mapEndpoint(getSoftwareVersions)
            .request({ body: value });
        }),
      )
      .subscribe((data) => {
        this.softwareVersionListSubject.next([
          ...(data.softwareVersions ?? []),
        ]);
      });

    this.triggerSystems$
      .pipe(
        switchMap((value) => {
          this.isSystemLoadingSubject.next(true);
          return this.mapApiService
            .mapEndpoint(getAdvancedSearchSystems)
            .request({ body: value });
        }),
      )
      .subscribe((data) => {
        {
          this.isSystemLoadingSubject.next(false);
          this.systemListSubject.next([...(data.systems ?? [])]);
          this.systemCountSubject.next(data.count ?? 0);
        }
      });

    this.triggerSystemsAnalytics$
      .pipe(
        switchMap((value) => {
          return this.mapApiService
            .mapEndpoint(createAdvancedSearchSystemsAnalytics)
            .request({ body: value });
        }),
      )
      .subscribe({
        next: (response) => {
          console.info('Analytics created successfully', response);
        },
        error: (err) => {
          console.info('Failed to create analytics', err);
        },
      });

    this.triggerTableAnalytics$
      .pipe(
        switchMap((value) => {
          return this.mapApiService
            .mapEndpoint(createAdvancedSearchTableAnalytics)
            .request({ body: value });
        }),
      )
      .subscribe({
        next: (response) => {
          console.info('Table analytics created successfully', response);
        },
        error: (err) => {
          console.info('Table failed to create analytics', err);
        },
      });
  }

  getCountryList(): void {
    this.triggerCountry$.next();
  }

  getCityList(payload: Array<string>): void {
    const cityRequest: CitiesRequest = {
      countries: payload,
    };
    this.triggerCity$.next(cityRequest);
  }

  getProductNameList(payload: Array<string>): void {
    const productNameRequest: ProductNamesRequest = {
      modalities: [...payload],
    };
    this.triggerProductName$.next(productNameRequest);
  }

  getSoftwareNameList(
    modalities: Array<string>,
    productNames: Array<string>,
  ): void {
    const softwareVersionRequest: SoftwareVersionsRequest = {
      modalities: modalities,
      productNames: productNames,
    };
    this.triggerSoftwareVersion$.next(softwareVersionRequest);
  }

  getSearchResults(advanceSearchRequestModel: AdvanceSearchRequestModel): void {
    let emailId = '';
    this.triggerSystems$.next(advanceSearchRequestModel);
    this.userService.mailId$.subscribe((x) => {
      emailId = x ?? '';
      const analyticsRequest: AdvanceSearchAnalyticsRequestModel = {
        cities: advanceSearchRequestModel.cities,
        countries: advanceSearchRequestModel.countries,
        customerNames: advanceSearchRequestModel.customerNames,
        emailId: emailId,
        equipmentIds: advanceSearchRequestModel.equipmentIds,
        equipmentTechIds: advanceSearchRequestModel.equipmentTechIds,
        functionalLocations: advanceSearchRequestModel.functionalLocations,
        materialNumbers: advanceSearchRequestModel.materialNumbers,
        modalityNames: advanceSearchRequestModel.modalityNames,
        productNames: advanceSearchRequestModel.productNames,
        serialNumbers: advanceSearchRequestModel.serialNumbers,
        softwareVersions: advanceSearchRequestModel.softwareVersions,
      };

      this.triggerSystemsAnalytics$.next(analyticsRequest);
    });
  }

  createTableAnalytics(
    advanceSearchRequestModel: AdvanceSearchResultModel,
  ): void {
    let emailId = '';
    this.userService.mailId$.subscribe((x) => {
      emailId = x ?? '';
      const analyticsRequest: AdvanceSearchTableAnalyticsModel = {
        email: emailId,
        materialNumbers: advanceSearchRequestModel.materialNumber,
        serialNumbers: advanceSearchRequestModel.serialNumber,
      };

      this.triggerTableAnalytics$.next(analyticsRequest);
    });
  }

  cleanData(): void {
    this.systemCountSubject.next(-1);
    this.systemListSubject.next(null);
    this.countryListSubject.next(null);
    this.cityListSubject.next(null);
    this.productNameListSubject.next(null);
    this.softwareVersionListSubject.next(null);
    this.isSystemLoadingSubject.next(false);
  }
}
